import { Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';
import { ATLEDI_ENABLED } from '@/features/ai-assistant/atlediAvailability';

interface ProjectAtlediInsightProps {
  projectName: string;
  eventCount: number;
  liveEventCount: number;
}

/**
 * PRD Xotski v2.0 §7.2 — Atledi project-level insight, smaller than the
 * Entity Home hero. Renders a one-sentence configuration-aware nudge.
 *
 * Until the backend AtlediService project endpoint ships (Phase 4), this
 * uses a deterministic local rule keyed off event counts. Shape and tone
 * match the PRD §16 narrative template ("At [comparable events] when [A]
 * was in place, you averaged N…"); the swap is one network call later.
 */
export function ProjectAtlediInsight({
  projectName,
  eventCount,
  liveEventCount,
}: ProjectAtlediInsightProps) {
  const t = useT();
  const message = pickLocalNudge(t, projectName, eventCount, liveEventCount);

  // Atledi is not released yet. An interactive widget that answers nothing is
  // worse than no widget — see ATLEDI_ENABLED. Placed AFTER every hook: an
  // early return above them changes the hook count between renders the moment
  // the flag flips, which is a crash, not a feature toggle.
  if (!ATLEDI_ENABLED) return null;

  return (
    <Card className="border-[var(--color-accent-ink)]/30 bg-[var(--color-accent-04)] p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-8 w-8 flex-none place-items-center rounded-full bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent-ink)]">
            {t('project.atledi.label')}
          </p>
          <p className="mt-0.5 text-sm leading-relaxed text-[var(--color-fg-primary)]">
            {message}
          </p>
        </div>
      </div>
    </Card>
  );
}

function pickLocalNudge(
  t: (key: string) => string,
  projectName: string,
  total: number,
  live: number,
): string {
  if (total === 0) {
    return t('project.atledi.emptyProject').replace('{name}', projectName);
  }
  if (live > 0) {
    return t('project.atledi.liveActivity')
      .replace('{name}', projectName)
      .replace('{live}', String(live));
  }
  return t('project.atledi.scheduled')
    .replace('{name}', projectName)
    .replace('{total}', String(total));
}
